import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { shipperProfileSchema, type ShipperProfile } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { X, CheckCircle2, Mail } from "lucide-react";
import { SignupHeader } from "@/components/SignupHeader";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import {
  classifyPartnerInviteResponse,
  SHIPPER_PARTNER_RESULTS_KEY,
  type PartnerInviteResult,
} from "@/lib/partnerInviteResults";

const FREIGHT_TYPE_OPTIONS = [
  "Palletized Freight",
  "Boxed Goods",
  "Electronics",
  "Food Grade",
  "Pharmaceuticals",
  "Hazmat",
  "Construction Materials",
  "Auto Parts",
  "Agricultural",
  "Other",
];

const MONTHLY_SHIPMENT_OPTIONS: { label: string; value: number }[] = [
  { label: "1–5", value: 5 },
  { label: "6–20", value: 20 },
  { label: "21–50", value: 50 },
  { label: "51–100", value: 100 },
  { label: "100+", value: 999 },
];

export default function SignupShipper() {
  useCsrfToken();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [freightTypes, setFreightTypes] = useState<string[]>([]);
  const [shippingLanes, setShippingLanes] = useState<string[]>([]);
  const [laneInput, setLaneInput] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual" | "free" | null>(null);
  const [signupPartners, setSignupPartners] = useState<Array<{name: string; email: string; partnerType: string}>>([]);
  const [signupPartnerName, setSignupPartnerName] = useState("");
  const [signupPartnerEmail, setSignupPartnerEmail] = useState("");
  const [signupPartnerType, setSignupPartnerType] = useState("");
  const [signupPartnerEmailCheck, setSignupPartnerEmailCheck] = useState<{exists: boolean; firstName?: string; lastName?: string; company?: string} | null>(null);
  const partnerInviteResultsRef = useRef<PartnerInviteResult[]>([]);
  const setupSessionAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      setupSessionAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing") as "monthly" | "annual" | "free" | null;
    if (billing === "monthly" || billing === "annual" || billing === "free") {
      setBillingCycle(billing);
    } else {
      toast({
        title: "Invalid access",
        description: "Please select a membership plan first",
        variant: "destructive",
      });
      setLocation("/select-membership/shipper");
    }
  }, [toast, setLocation]);

  const form = useForm<ShipperProfile>({
    resolver: zodResolver(shipperProfileSchema),
    defaultValues: {
      userType: "Shipper",
      company: "",
      phone: "",
      monthlyShipments: undefined,
      dotNumber: "",
      freightTypes: [],
      shippingLanes: [],
      hasShipperAcknowledgment: undefined as unknown as true,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: ShipperProfile) => {
      if (!billingCycle) {
        throw new Error("Billing cycle not selected");
      }
      const searchParams = new URLSearchParams(window.location.search);
      const billing = searchParams.get("billing") ?? "monthly";
      await apiRequest("POST", "/api/profile/complete", {
        ...data,
        freightTypes,
        shippingLanes,
        billingCycle,
        billing,
      });
    },
    onSuccess: async () => {
      if (window.fbq) window.fbq('track', 'CompleteRegistration');

      // Send partner invites collected during signup
      const partnerInviteResults: PartnerInviteResult[] = [];
      if (signupPartners.length > 0) {
        for (const partner of signupPartners) {
          try {
            const response = await fetch("/api/partner-invites", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
              body: JSON.stringify({
                name: partner.name,
                email: partner.email,
                partnerType: partner.partnerType,
              }),
            });
            const result = response.ok ? await response.json() : null;
            partnerInviteResults.push({
              name: partner.name,
              email: partner.email,
              status: classifyPartnerInviteResponse(response.ok, result?.status),
            });
          } catch (err) {
            console.error("Failed to invite partner during signup:", err);
            partnerInviteResults.push({
              name: partner.name,
              email: partner.email,
              status: "error",
            });
          }
        }
      }
      partnerInviteResultsRef.current = partnerInviteResults;
      try {
        sessionStorage.setItem(SHIPPER_PARTNER_RESULTS_KEY, JSON.stringify(partnerInviteResults));
      } catch (err) {
        console.warn("Unable to preserve partner invite results:", err);
      }

      if (billingCycle === "free") {
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setLocation("/signup-welcome");
        return;
      }
      try {
        const controller = new AbortController();
        setupSessionAbortRef.current = controller;
        const res = await fetch("/api/create-setup-session", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
          body: JSON.stringify({ billingCycle: billingCycle || "monthly", membershipTier: "Paid" }),
          signal: controller.signal,
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No checkout URL returned");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to create Stripe setup session:", err);
        toast({
          title: "Payment Setup Required",
          description: "We couldn't set up payment collection. Please try again. A credit card is required to complete signup.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleFreightType = (type: string) => {
    setFreightTypes((prev) => {
      const updated = prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type];
      form.setValue("freightTypes", updated, { shouldValidate: true });
      return updated;
    });
  };

  const addShippingLane = () => {
    if (laneInput.trim() && !shippingLanes.includes(laneInput.trim())) {
      setShippingLanes([...shippingLanes, laneInput.trim()]);
      setLaneInput("");
    }
  };

  const removeShippingLane = (lane: string) => {
    setShippingLanes(shippingLanes.filter((l) => l !== lane));
  };

  const handleSignupPartnerEmailCheck = async (email: string) => {
    if (!email || !email.includes("@")) { setSignupPartnerEmailCheck(null); return; }
    try {
      const res = await fetch(`/api/users/check-email?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setSignupPartnerEmailCheck(data);
    } catch { setSignupPartnerEmailCheck(null); }
  };

  const handleAddSignupPartner = () => {
    if (!signupPartnerName.trim() || !signupPartnerEmail.trim() || !signupPartnerType) return;
    setSignupPartners(prev => [...prev, {
      name: signupPartnerName.trim(),
      email: signupPartnerEmail.trim().toLowerCase(),
      partnerType: signupPartnerType,
    }]);
    setSignupPartnerName("");
    setSignupPartnerEmail("");
    setSignupPartnerType("");
    setSignupPartnerEmailCheck(null);
  };

  const handleRemoveSignupPartner = (index: number) => {
    setSignupPartners(prev => prev.filter((_, i) => i !== index));
  };

  if (!billingCycle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SignupHeader backUrl="/select-membership/shipper" />
      <div className="flex items-center justify-center p-4 pt-24 min-h-screen">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <CardTitle className="text-2xl">Complete Your Shipper Profile</CardTitle>
            <CardDescription>
              Tell us about your shipping needs to help carriers find and serve you better
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-0">

                {/* Section 1 — Business Information */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Business Information</p>

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Your Company LLC" {...field} data-testid="input-company" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input placeholder="(555) 123-4567" {...field} data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dotNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>DOT Number <span className="text-muted-foreground font-normal">(if applicable)</span></FormLabel>
                        <FormControl>
                          <Input placeholder="1234567" {...field} data-testid="input-dot" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section 2 — Shipping Profile */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Shipping Profile</p>

                  <FormField
                    control={form.control}
                    name="monthlyShipments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Average Monthly Shipments</FormLabel>
                        <Select
                          onValueChange={(val) => field.onChange(parseInt(val))}
                          value={field.value !== undefined ? String(field.value) : ""}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-monthly-shipments">
                              <SelectValue placeholder="Select a range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {MONTHLY_SHIPMENT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-3">
                    <FormLabel>Freight Types</FormLabel>
                    <div className="grid grid-cols-2 gap-1.5">
                      {FREIGHT_TYPE_OPTIONS.map((type) => (
                        <div key={type} className="flex items-center gap-2">
                          <Checkbox
                            id={`freight-${type}`}
                            checked={freightTypes.includes(type)}
                            onCheckedChange={() => toggleFreightType(type)}
                            data-testid={`checkbox-freight-${type}`}
                          />
                          <label htmlFor={`freight-${type}`} className="text-sm cursor-pointer">
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Primary Shipping Lanes</FormLabel>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Chicago to Dallas, NY to LA"
                        value={laneInput}
                        onChange={(e) => setLaneInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addShippingLane())}
                        data-testid="input-shipping-lane"
                      />
                      <Button type="button" onClick={addShippingLane} data-testid="button-add-lane">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {shippingLanes.map((lane) => (
                        <Badge key={lane} variant="secondary" className="gap-1" data-testid={`badge-lane-${lane}`}>
                          {lane}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeShippingLane(lane)} />
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section: Add Your Freight Partners */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="font-medium">Add your freight partners</p>
                  <p className="text-sm text-muted-foreground">
                    Add the brokers and carriers you already work with so they can bid on your loads. Once you post a shipment, they'll be notified automatically.
                  </p>

                  {/* Partner list */}
                  {signupPartners.length > 0 && (
                    <div className="space-y-2">
                      {signupPartners.map((partner, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                              {partner.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{partner.name}</p>
                              <p className="text-xs text-muted-foreground">{partner.email} · {partner.partnerType === "broker" ? "Broker" : "Carrier"}</p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSignupPartner(index)}
                            data-testid={`button-remove-signup-partner-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Inline add partner form */}
                  <div className="space-y-2 p-4 border rounded-lg bg-muted/20">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Full name"
                        value={signupPartnerName}
                        onChange={(e) => setSignupPartnerName(e.target.value)}
                        data-testid="input-signup-partner-name"
                      />
                      <Select value={signupPartnerType} onValueChange={setSignupPartnerType}>
                        <SelectTrigger data-testid="select-signup-partner-type">
                          <SelectValue placeholder="Broker or Carrier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="broker">Broker</SelectItem>
                          <SelectItem value="carrier">Carrier</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={signupPartnerEmail}
                        onChange={(e) => {
                          setSignupPartnerEmail(e.target.value);
                          setSignupPartnerEmailCheck(null);
                        }}
                        onBlur={(e) => handleSignupPartnerEmailCheck(e.target.value)}
                        className="flex-1"
                        data-testid="input-signup-partner-email"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddSignupPartner}
                        disabled={!signupPartnerName.trim() || !signupPartnerEmail.trim() || !signupPartnerType}
                        data-testid="button-add-signup-partner"
                      >
                        Add
                      </Button>
                    </div>

                    {/* Email check feedback */}
                    {signupPartnerEmailCheck && (
                      <div className="flex items-start gap-2 mt-1">
                        {signupPartnerEmailCheck.exists ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-green-700">
                              <strong>{signupPartnerEmailCheck.firstName} {signupPartnerEmailCheck.lastName}</strong>
                              {signupPartnerEmailCheck.company ? ` — ${signupPartnerEmailCheck.company}` : ""} · Already on Fill The Trucks
                            </p>
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-blue-700">
                              <strong>{signupPartnerEmail}</strong> — Invite to Fill The Trucks
                              <br />
                              <span className="text-muted-foreground">We'll send a free invite to join and bid on your load.</span>
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {signupPartners.length === 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full text-muted-foreground text-sm"
                      onClick={() => {}}
                      data-testid="button-skip-signup-partners"
                    >
                      Skip for now — I'll add partners later
                    </Button>
                  )}
                  {signupPartners.length > 0 && (
                    <p className="text-xs text-muted-foreground text-center">
                      You can add more partners anytime from the My Partners page in your dashboard.
                    </p>
                  )}
                </div>

                {/* Section 3 — Acknowledgment */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Acknowledgment</p>

                  <FormField
                    control={form.control}
                    name="hasShipperAcknowledgment"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 rounded-md bg-muted p-4">
                          <FormControl>
                            <Checkbox
                              id="hasShipperAcknowledgment"
                              checked={field.value === true}
                              onCheckedChange={(checked) => field.onChange(checked === true ? true : undefined)}
                              data-testid="checkbox-shipper-acknowledgment"
                            />
                          </FormControl>
                          <label htmlFor="hasShipperAcknowledgment" className="text-sm font-medium cursor-pointer leading-snug">
                            I understand that Fill The Trucks does not provide cargo insurance and that I am responsible for obtaining appropriate insurance coverage for my shipments.
                          </label>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="border-t pt-5">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={mutation.isPending}
                    data-testid="button-complete-profile"
                  >
                    {mutation.isPending ? "Saving..." : "Complete Profile"}
                  </Button>
                </div>

              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
